import React, { useState } from 'react';
import { useLanguage } from '@/LanguageContext';
import { motion } from 'framer-motion';
import { Quote, ChevronLeft, ChevronRight } from 'lucide-react';

const Testimonials = () => {
  const { t } = useLanguage();
  const [current, setCurrent] = useState(0);
  const items = t('testimonials.items');

  const fadeUp = {
    hidden: { opacity: 0, y: 40 },
    visible: (i) => ({
      opacity: 1, y: 0,
      transition: { duration: 0.6, delay: i * 0.1, ease: [0.22, 1, 0.36, 1] },
    }),
  };

  const next = () => setCurrent((prev) => (prev + 1) % items.length);
  const prev = () => setCurrent((prev) => (prev - 1 + items.length) % items.length);

  return (
    <section id="testimonials" data-testid="testimonials-section" className="py-24 md:py-32 bg-white">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          <div>
            <motion.p
              variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} custom={0}
              className="text-[#C5A55A] text-sm font-mono tracking-[0.3em] uppercase mb-4"
            >
              {t('testimonials.overline')}
            </motion.p>
            <motion.h2
              variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} custom={1}
              className="font-heading text-4xl md:text-5xl font-semibold tracking-tight text-[#0A1628] mb-8"
              data-testid="testimonials-title"
            >
              {t('testimonials.title')}
            </motion.h2>

            <div className="flex gap-3">
              {items.map((_, i) => (
                <button
                  key={i}
                  onClick={() => setCurrent(i)}
                  className={`w-12 h-1 transition-all duration-300 ${
                    i === current ? 'bg-[#C5A55A]' : 'bg-slate-200'
                  }`}
                  data-testid={`testimonial-dot-${i}`}
                  aria-label={`Testimonial ${i + 1}`}
                />
              ))}
            </div>
          </div>

          <motion.div
            variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} custom={2}
            className="relative"
          >
            <div className="bg-[#F4F7FB] p-10 md:p-12 relative">
              <Quote className="w-10 h-10 text-[#C5A55A]/30 absolute top-6 right-6" />
              <p className="text-[#0A1628] text-lg md:text-xl leading-relaxed mb-8 font-medium" data-testid="testimonial-text">
                "{items[current].text}"
              </p>
              <div className="border-t border-slate-200 pt-6">
                <p className="font-heading font-semibold text-[#0A1628]" data-testid="testimonial-author">{items[current].author}</p>
                <p className="text-[#C5A55A] text-sm font-mono tracking-wider">{items[current].role}</p>
              </div>
            </div>

            <div className="flex gap-3 mt-6 justify-end">
              <button
                onClick={prev}
                className="w-12 h-12 flex items-center justify-center border border-slate-200 hover:border-[#C5A55A] hover:bg-[#C5A55A] hover:text-[#0A1628] text-slate-500 transition-all duration-300"
                data-testid="testimonial-prev"
                aria-label="Previous testimonial"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              <button
                onClick={next}
                className="w-12 h-12 flex items-center justify-center border border-slate-200 hover:border-[#C5A55A] hover:bg-[#C5A55A] hover:text-[#0A1628] text-slate-500 transition-all duration-300"
                data-testid="testimonial-next"
                aria-label="Next testimonial"
              >
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Testimonials;
